import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { GroupPayerNetworkStatus } from 'models/GroupPayerNetworkStatus';
import { destroyGroupPayerNetworkStatus } from 'services/api/groupPayerNetworkStatuses';
import { validationErrorMessage } from 'services/api/validationErrors';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

import { type NetworkStatusKind, removedElsewhere } from './NetworkStatusTable';

type RemoveNetworkStatusModalProps = {
  kind: NetworkStatusKind;
  row: GroupPayerNetworkStatus;
  onClose: () => void;
  onRemoved?: (id: string) => void;
};

export default function RemoveNetworkStatusModal({ kind, row, onClose, onRemoved }: RemoveNetworkStatusModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const onPayerPage = kind === 'payer';
  const [removed, from] = onPayerPage ? [row.groupName, row.stediPayerName] : [row.stediPayerName, row.groupName];

  const finish = () => {
    invalidateQueries({ groupPayerNetworkStatuses: true });
    onRemoved?.(row.id);
    onClose();
  };

  // Callbacks on the hook, not per-call: React Query drops per-call callbacks if the header ×
  // unmounts the modal mid-request. A 404 means someone else removed it first, which is the outcome
  // asked for, so it finishes the same way rather than leaving a Remove that can never succeed.
  const { mutate, isPending } = useMutateWithToast({
    mutationFn: destroyGroupPayerNetworkStatus,
    errorToast: (error) =>
      removedElsewhere(error) ? `${removed} was already removed from ${from}.` : validationErrorMessage(error),
    onSuccess: () => {
      addToast({ text: `${removed} removed from ${from}` });
      finish();
    },
    onError: (error) => {
      if (removedElsewhere(error)) finish();
    },
  });

  const handleRemove = () => mutate(row.id);

  return (
    <StandardModal
      title={onPayerPage ? 'Remove Facility from Payer?' : 'Remove Payer from Facility?'}
      colorTheme={ColorThemeName.PrimaryRed}
      onCancel={onClose}>
      <StandardModal.RecordName>{onPayerPage ? `Payer: ${from}` : `Facility: ${from}`}</StandardModal.RecordName>
      <Content>
        {`${removed} will be removed from ${from}, and its settings for all payer types will be cleared. You can add it back later.`}
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='danger' onClick={handleRemove} loading={isPending} disabled={isPending}>
          Remove
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;
